import React, { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa"; // ícone de lixeira
import { API_URL } from '../../../environment';

interface PerguntaResposta {
  pergunta: string;
  resposta: string;
}

interface Servico {
  id: number;
  procedimento: string;
  tempo: string;
  unidade: "minutos" | "horas" | "dias";
  valor: string;
  descricao: string;
}

interface ServicoInput {
  procedimento: string;
  tempo: string;
  unidade: "minutos" | "horas" | "dias";
  valor: string;
  descricao: string;
}


export default function Agendamento() {
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [novoServico, setNovoServico] = useState<ServicoInput>({
    procedimento: "",
    tempo: "",
    unidade: "minutos", // valor padrão
    valor: "",
    descricao: "",
  });
  const [agendamentoHabilitado, setAgendamentoHabilitado] = useState<boolean>(true);
  const [carregandoStatus, setCarregandoStatus] = useState<boolean>(true);


  const [perguntasRespostas, setPerguntasRespostas] = useState<PerguntaResposta[]>([]);

  useEffect(() => {
    const id_cliente = localStorage.getItem("id_cliente");
    if (!id_cliente) return;

    // Buscar serviços
    fetch(`${API_URL}/admin/agendamento/servicos?id_time=${id_cliente}`)
      .then(res => res.json())
      .then(data => {
        setServicos(data || []);
      })
      .catch(console.error);

    // Buscar perguntas e respostas
    fetch(`${API_URL}/admin/agendamento/perguntas?id_time=${id_cliente}`)
      .then(res => res.json())
      .then(data => {
        const formatado = data.map((item: any) => ({
          pergunta: item.perguntas,
          resposta: item.respostas
        }));
        setPerguntasRespostas(formatado);
      })
      .catch(console.error);

    // Buscar status do agendamento
    fetch(`${API_URL}/admin/agendamento/status?id_cliente=${id_cliente}`)
      .then(res => res.json())
      .then(data => {
        setAgendamentoHabilitado(data.agendamento === "Sim");
        setCarregandoStatus(false);
      })
      .catch(() => setCarregandoStatus(false));

  }, []);

  const toggleAgendamento = async () => {
    const id_cliente = localStorage.getItem("id_cliente");
    if (!id_cliente) return;

    const novoStatus = !agendamentoHabilitado;
    setAgendamentoHabilitado(novoStatus);

    try {
      await fetch(`${API_URL}/admin/agendamento/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: Number(id_cliente),
          habilitado: novoStatus
        }),
      });
    } catch (e) {
      alert("Erro ao atualizar status.");
      setAgendamentoHabilitado(!novoStatus); // Reverte visualmente em caso de erro
    }
  };


  const handleAdicionarPergunta = () => {
    setPerguntasRespostas([...perguntasRespostas, { pergunta: "", resposta: "" }]);
  };

  const handleAdicionarServico = async () => {
    const id_cliente = localStorage.getItem("id_cliente");
    if (!id_cliente) {
      alert("ID do cliente não encontrado.");
      return;
    }

    const tempoNumerico = parseInt(novoServico.tempo);
    if (isNaN(tempoNumerico) || tempoNumerico <= 0) {
      alert("Tempo deve ser um número positivo.");
      return;
    }

    let tempoEmMinutos = tempoNumerico;
    if (novoServico.unidade === "horas") tempoEmMinutos *= 60;
    if (novoServico.unidade === "dias") tempoEmMinutos *= 1440;

    const payload = {
      id_time: Number(id_cliente),
      procedimento: novoServico.procedimento,
      tempo: tempoEmMinutos.toString(), // deve ser "tempo"
      valor: novoServico.valor
    };

    try {
      const res = await fetch(`${API_URL}/admin/agendamento/servicos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const resultado = await res.json();

      if (res.ok) {
        setNovoServico({
          procedimento: "",
          tempo: "",
          unidade: "minutos",
          valor: "",
          descricao: "",
        });
        alert("Serviço adicionado com sucesso!");

        const responseServicos = await fetch(`${API_URL}/admin/agendamento/servicos?id_time=${id_cliente}`);
        const servicosAtualizados = await responseServicos.json();
        setServicos(servicosAtualizados || []);
      } else {
        console.error(resultado);
        alert("Erro ao adicionar serviço: " + (resultado.error || "Erro desconhecido."));
      }
    } catch (err) {
      console.error(err);
      alert("Erro ao adicionar serviço.");
    }
  };

  const handleDeletarServico = async (id: number) => {
    const id_cliente = localStorage.getItem("id_cliente");
    if (!id_cliente) return;

    try {
      const res = await fetch(`${API_URL}/admin/agendamento/servicos/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const err = await res.json();
        alert("Erro ao deletar serviço: " + (err.error || "Erro desconhecido"));
        return;
      }

      // Atualiza a lista local removendo o serviço deletado
      setServicos(servicos.filter(s => s.id !== id));

    } catch (e) {
      alert("Erro ao deletar serviço: " + e);
    }
  };

  function formatarValor(valor: string): string {
    // Converte string para número e formata com 2 casas decimais e vírgula
    const num = Number(valor);
    if (isNaN(num)) return valor; // caso não seja número, retorna original
    return num.toFixed(2).replace('.', ',');
  }

  function formatarTempo(tempoMinutosStr: string): string {
    const tempo = parseInt(tempoMinutosStr, 10);
    if (isNaN(tempo)) return tempoMinutosStr;

    if (tempo >= 1440) { // 24 horas
      const dias = (tempo / 1440).toFixed(1);
      return `${dias} dia${Number(dias) !== 1 ? 's' : ''}`;
    }
    if (tempo >= 60) {
      const horas = (tempo / 60).toFixed(1);
      return `${horas} hora${Number(horas) !== 1 ? 's' : ''}`;
    }
    return `${tempo} minuto${tempo !== 1 ? 's' : ''}`;
  }

  return (
    <div className="page-wrapper">
      <div className="d-flex align-items-center gap-3">
        <h2 className="mb-1">Agendamento</h2>
      </div>

      <div className="content">
        {/* Seção de Serviços */}
        <div className="card">
          <div className="card-body">
            <h4 className="mb-3">Serviços</h4>

            <div className="row mb-3 g-2">
              <div className="col-12 col-md-3">
                <input
                  className="form-control"
                  placeholder="Procedimento"
                  value={novoServico.procedimento}
                  onChange={(e) => setNovoServico({ ...novoServico, procedimento: e.target.value })}
                />
              </div>
              <div className="col-6 col-md-2">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Tempo"
                  value={novoServico.tempo}
                  onChange={(e) => setNovoServico({ ...novoServico, tempo: e.target.value })}
                />
              </div>
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={novoServico.unidade}
                  onChange={(e) => setNovoServico({ ...novoServico, unidade: e.target.value as "minutos" | "horas" | "dias" })}
                >
                  <option value="minutos">Minutos</option>
                  <option value="horas">Horas</option>
                  <option value="dias">Dias</option>
                </select>
              </div>
              <div className="col-12 col-md-2">
                <input
                  className="form-control"
                  placeholder="Valor"
                  value={novoServico.valor}
                  onChange={(e) => setNovoServico({ ...novoServico, valor: e.target.value })}
                />
              </div>
            </div>

            <button className="btn btn-primary mb-3" onClick={handleAdicionarServico}>
              Adicionar Serviço
            </button>

            <div>
              <h6>Serviços adicionados:</h6>
              <ul className="list-group">
                {servicos.map((s) => (
                  <li key={s.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      <strong>{s.procedimento}</strong> - {formatarTempo(s.tempo)} - R$ {formatarValor(s.valor)}
                    </span>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeletarServico(s.id)}
                      title="Excluir serviço"
                    >
                      <FaTrash />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Seção de Perguntas e Respostas */}
        <div className="card mt-4">
          <div className="card-body">
            <h4 className="mb-3">Perguntas e Respostas</h4>
            {perguntasRespostas.map((pr, i) => (
              <div className="row mb-2 g-2" key={i}>
                <div className="col-12 col-md-6">
                  <input
                    className="form-control"
                    placeholder="Pergunta"
                    value={pr.pergunta}
                    onChange={(e) => {
                      const novo = [...perguntasRespostas];
                      novo[i].pergunta = e.target.value;
                      setPerguntasRespostas(novo);
                    }}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="form-control"
                    placeholder="Resposta"
                    value={pr.resposta}
                    onChange={(e) => {
                      const novo = [...perguntasRespostas];
                      novo[i].resposta = e.target.value;
                      setPerguntasRespostas(novo);
                    }}
                  />
                </div>
              </div>
            ))}

            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-outline-primary" onClick={handleAdicionarPergunta}>
                + Adicionar pergunta
              </button>

              <button className="btn btn-success" onClick={async () => {
                const id_cliente = localStorage.getItem("id_cliente");
                if (!id_cliente) {
                  alert("ID do cliente não encontrado.");
                  return;
                }

                try {
                  const res = await fetch(`${API_URL}/admin/agendamento/perguntas`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                      id_time: Number(id_cliente),
                      perguntas_respostas: perguntasRespostas
                    })
                  });

                  const resultado = await res.json();

                  if (res.ok) {
                    alert("Perguntas e respostas salvas com sucesso!");
                  } else {
                    console.error(resultado);
                    alert("Erro ao salvar: " + (resultado.error || "Erro desconhecido."));
                  }
                } catch (err) {
                  console.error(err);
                  alert("Erro ao salvar perguntas.");
                }
              }}>
                ✔ Aplicar Perguntas
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
